import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { AppBar } from "../../components/AppShell";
import { useToast } from "../../components/Toast";
import { request } from "../../lib/api";
import type { CourseDetail, ExamOut } from "../../lib/types";
import { DetailsTab } from "./editor/DetailsTab";
import { LessonsTab } from "./editor/LessonsTab";
import { ExamTab } from "./editor/ExamTab";
import { PublishTab } from "./editor/PublishTab";

const META_CSS = `
.meta-chip { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 999px; font-weight: 700; font-size: .82rem; cursor: pointer; user-select: none; border: 1px solid transparent; }
.meta-chip input { display: none; }
.meta-chip.medic { background: var(--c-primary-light); color: var(--c-primary-dark); }
.meta-chip.rezident { background: #fdf1de; color: #8a5a04; }
.meta-chip.asistent { background: #ede4fa; color: #4a2c91; }
.meta-chip.off { background: var(--c-bg); color: var(--c-muted); border-color: var(--c-line); }
.tab-panel { display: block; }
`;

const AUDIENCES = ["medic", "rezident", "asistent"] as const;
const AUD_META: Record<string, { icon: string; label: string }> = {
  medic: { icon: "fa-user-doctor", label: "Medici" },
  rezident: { icon: "fa-stethoscope", label: "Rezidenți" },
  asistent: { icon: "fa-hand-holding-medical", label: "Asistenți" },
};

export type EditorTab = "detalii" | "lectii" | "examen" | "publicare";

export function CourseEditor() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<EditorTab>("detalii");

  const courseQuery = useQuery({
    queryKey: ["course", id],
    queryFn: () => request<CourseDetail>(`/courses/${id}`),
    enabled: !!id,
  });
  const examQuery = useQuery({
    queryKey: ["course", id, "exam-admin"],
    queryFn: () => request<ExamOut>(`/courses/${id}/exam/admin`),
    enabled: !!id,
    retry: false,
  });

  const updateAudienceMut = useMutation({
    mutationFn: (audiences: string[]) => request<CourseDetail>(`/courses/${id}`, { method: "PATCH", body: { audiences } }),
    onSuccess: (updated) => {
      qc.setQueryData(["course", id], updated);
      toast("Public țintă actualizat", "success");
    },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  const course = courseQuery.data;
  const exam = examQuery.data;
  const lessonsCount = course?.lessons.length || 0;
  const questionsCount = exam?.questions.length || 0;

  function statusPill(s?: string) {
    if (s === "published") return <span className="pill success">Publicat</span>;
    if (s === "draft") return <span className="pill warning">Schiță</span>;
    return <span className="pill neutral">Arhivat</span>;
  }

  function toggleAudience(role: string) {
    if (!course) return;
    const cur = new Set(course.audiences || []);
    if (cur.has(role)) cur.delete(role); else cur.add(role);
    updateAudienceMut.mutate(Array.from(cur));
  }

  if (courseQuery.isLoading) {
    return (
      <>
        <AppBar crumbs={[{ label: "Cursuri", to: "/admin/cursuri" }]} />
        <div className="content"><div className="card text-muted">Se încarcă editorul…</div></div>
      </>
    );
  }
  if (!course) {
    return (
      <>
        <AppBar crumbs={[{ label: "Cursuri", to: "/admin/cursuri" }]} />
        <div className="content"><div className="card text-muted">Curs invalid. <Link to="/admin/cursuri">Înapoi</Link></div></div>
      </>
    );
  }

  return (
    <>
      <style>{META_CSS}</style>
      <AppBar
        crumbs={[{ label: "Cursuri", to: "/admin/cursuri" }, { label: course.title }]}
        rightExtras={
          <Link to={`/student/curs/${course.id}`} className="btn btn-ghost btn-sm" target="_blank" rel="noreferrer">
            <i className="fa-solid fa-eye"></i> Previzualizează
          </Link>
        }
      />
      <div className="content">
        <div className="page-head">
          <div><h1>Editor curs</h1><p>Configurează detaliile, lecțiile și examenul cursului.</p></div>
        </div>

        <div className="card" style={{ marginBottom: 18, display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <small className="muted" style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", fontSize: ".72rem" }}>Public țintă</small>
            <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
              {AUDIENCES.map((a) => {
                const on = (course.audiences || []).includes(a);
                const meta = AUD_META[a];
                return (
                  <label key={a} className={"meta-chip " + (on ? a : "off")} onClick={(e) => { e.preventDefault(); toggleAudience(a); }}>
                    <input type="checkbox" checked={on} readOnly />
                    <i className={`fa-solid ${meta.icon}`}></i> {meta.label}
                  </label>
                );
              })}
            </div>
          </div>
          <Meta label="Categorie" value={course.category} />
          <Meta label="Lecții" value={lessonsCount} />
          <Meta label="Status" value={statusPill(course.status)} />
        </div>

        <div className="tabs">
          <div className={"tab" + (tab === "detalii" ? " active" : "")} onClick={() => setTab("detalii")}>
            <i className="fa-solid fa-circle-info"></i> Detalii curs
          </div>
          <div className={"tab" + (tab === "lectii" ? " active" : "")} onClick={() => setTab("lectii")}>
            <i className="fa-solid fa-list-check"></i> Lecții ({lessonsCount})
          </div>
          <div className={"tab" + (tab === "examen" ? " active" : "")} onClick={() => setTab("examen")}>
            <i className="fa-solid fa-clipboard-check"></i> Examen ({questionsCount} întrebări)
          </div>
          <div className={"tab" + (tab === "publicare" ? " active" : "")} onClick={() => setTab("publicare")}>
            <i className="fa-solid fa-rocket"></i> Setări publicare
          </div>
        </div>

        {tab === "detalii" && <DetailsTab course={course} />}
        {tab === "lectii" && <LessonsTab course={course} />}
        {tab === "examen" && <ExamTab course={course} exam={exam} />}
        {tab === "publicare" && <PublishTab course={course} exam={exam} />}
      </div>
    </>
  );
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <small className="muted" style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", fontSize: ".72rem" }}>{label}</small>
      <div style={{ marginTop: 6 }}>{typeof value === "string" || typeof value === "number" ? <strong>{value}</strong> : value}</div>
    </div>
  );
}
